/**
 * Endpoints de *Planes y Módulos* (`/api/planes*`).
 *
 * El módulo es exclusivo del administrador principal (usuario 1), como en el
 * panel anterior: la API responde 403 al resto y la interfaz lo avisa.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogoModulos,
  Plan,
  PlanEdicionPayload,
  PlanPayload,
  PlanesRespuesta,
} from './planes.types';

export const planesKeys = {
  all: ['planes'] as const,
  listado: ['planes', 'listado'] as const,
  modulos: ['planes', 'modulos'] as const,
  modulosDelPlan: (id: number) => ['planes', 'plan-modulos', id] as const,
} as const;

export const planesApi = {
  listar: (signal?: AbortSignal): Promise<PlanesRespuesta> =>
    apiClient.get<PlanesRespuesta>('/api/planes', { signal }),

  /** Catálogo completo de módulos (pantalla *Módulos del Sistema*). */
  catalogo: (signal?: AbortSignal): Promise<CatalogoModulos> =>
    apiClient.get<CatalogoModulos>('/api/planes/modulos', { signal }),

  modulosDelPlan: (id: number, signal?: AbortSignal): Promise<{ plan_id: number; modulos: number[] }> =>
    apiClient.get<{ plan_id: number; modulos: number[] }>(`/api/planes/${id}/modulos`, { signal }),

  crear: (payload: PlanPayload): Promise<{ plan: Plan; mensaje: string }> =>
    apiClient.post<{ plan: Plan; mensaje: string }>('/api/planes', { body: payload }),

  actualizar: (
    id: number,
    payload: PlanEdicionPayload,
  ): Promise<{ plan: Plan; mensaje: string }> =>
    apiClient.put<{ plan: Plan; mensaje: string }>(`/api/planes/${id}`, { body: payload }),

  eliminar: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/planes/${id}`),

  duplicar: (
    id: number,
    payload: { nombre: string; codigo: string },
  ): Promise<{ plan: Plan; modulos: number; mensaje: string }> =>
    apiClient.post<{ plan: Plan; modulos: number; mensaje: string }>(`/api/planes/${id}/duplicar`, {
      body: payload,
    }),

  /** Reemplaza la selección completa de módulos del plan. */
  guardarModulos: (id: number, modulos: number[]): Promise<{ mensaje: string; modulos: number }> =>
    apiClient.put<{ mensaje: string; modulos: number }>(`/api/planes/${id}/modulos`, {
      body: { modulos },
    }),
} as const;

/** Planes con su número de módulos. */
export function usePlanes(enabled = true) {
  return useQuery({
    queryKey: planesKeys.listado,
    queryFn: ({ signal }) => planesApi.listar(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Catálogo de módulos del sistema (sólo para administradores principales). */
export function useCatalogoModulos(enabled = true) {
  return useQuery({
    queryKey: planesKeys.modulos,
    queryFn: ({ signal }) => planesApi.catalogo(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Ids de los módulos asignados a un plan. */
export function useModulosDelPlan(id: number | null, enabled = true) {
  return useQuery({
    queryKey: planesKeys.modulosDelPlan(id ?? 0),
    queryFn: ({ signal }) => planesApi.modulosDelPlan(id as number, signal),
    enabled: enabled && Boolean(id),
    staleTime: 0,
  });
}

/** Mutaciones de planes (alta, edición, baja, duplicado y módulos). */
export function useMutarPlanes() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: planesKeys.all });

  return {
    crear: useMutation({ mutationFn: planesApi.crear, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: PlanEdicionPayload }) =>
        planesApi.actualizar(id, payload),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: planesApi.eliminar, onSuccess: refrescar }),
    duplicar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: { nombre: string; codigo: string } }) =>
        planesApi.duplicar(id, payload),
      onSuccess: refrescar,
    }),
    guardarModulos: useMutation({
      mutationFn: ({ id, modulos }: { id: number; modulos: number[] }) =>
        planesApi.guardarModulos(id, modulos),
      onSuccess: refrescar,
    }),
  };
}
